import * as R from 'ramda';
import * as Yup from 'yup';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import {
  STATE_OPTIONS,
  COUNTRY_OPTIONS,
  ITEM_DIMENSIONS_OPTIONS,
  ACCESSORIAL_MATRIX_TYPE_OPTIONS,
  getSharedAccessorialAutoApplyToTypeOptions,
  DEFAULT_DRIVER_ASSESSORIAL_RATE_TYPE_OPTIONS,
  DEFAULT_ACCESSORIAL_FORM_RATE_UNIT_GROUP_OPTIONS,
} from '../../../helpers/options';
//////////////////////////////////////////////////

const inputWrapperStyles = {
  ml: 20,
  mb: 25,
  width: 230,
};

const geoFencingZoneInputWrapperStyles = {
  mb: 25,
  width: 200,
};

export const defaultFuelIndexVariableFields = () => (
  {
    id: G.genShortId(),
    [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES_RATE]: null,
    [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES_VALUE_TO]: null,
    [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES_VALUE_FROM]: null,
  }
);

export const defaultMatrixVariableFields = () => (
  {
    id: G.genShortId(),
    [GC.FIELD_CHARGE_RATE]: null,
    [GC.FIELD_VALUE_TO]: null,
    [GC.FIELD_VALUE_FROM]: null,
  }
);

export const defaultAccessorialFieldSettings = {
  [GC.FIELD_NAME]: null,
  useGeoFencingZone: false,
  [GC.FIELD_CURRENCY]: null,
  [GC.FIELD_MAX_STOPS]: null,
  [GC.FIELD_MIN_STOPS]: null,
  [GC.FIELD_CHARGE_RATE]: null,
  [GC.FIELD_TIME_PERIOD]: null,
  [GC.FIELD_CHARGE_MIN_RATE]: null,
  [GC.FIELD_CHARGE_MAX_RATE]: null,
  [GC.FIELD_CHARGE_RATE_UNIT]: null,
  [GC.FIELD_CHARGE_RATE_TYPE]: null,
  [GC.FIELD_ACCESSORIAL_DISTRICT]: null,
  [GC.FIELD_ACCESSORIAL_MATRIX_UOM]: null,
  [GC.FIELD_ACCESSORIAL_MATRIX_TYPE]: null,
  [GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES]: [],
  [GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX]: false,
  [GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID]: null,
  [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES]: [],
};

const fuelIndexVariablesValidationSchemaObject = {
  [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES_VALUE_TO]: Yup.number()
    .nullable(true)
    .required(G.getRequiredLocaleTxt())
    .positive(G.getShouldBePositiveLocaleTxt())
    .max(9999999, G.getShouldBeLessOrEqualLocaleTxt(999999))
    .typeError('Should be a ${type}'), // eslint-disable-line
  [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES_VALUE_FROM]: Yup.number()
    .nullable(true)
    .required(G.getRequiredLocaleTxt())
    .min(0, G.getShouldBeFromToLocaleTxt(0, 999999))
    .max(9999999, G.getShouldBeLessOrEqualLocaleTxt(999999))
    .typeError('Should be a ${type}'), // eslint-disable-line
  [GC.FIELD_CHARGE_RATE]: Yup.number()
    .nullable(true)
    .required(G.getRequiredLocaleTxt())
    .min(0, G.getShouldBeFromToLocaleTxt(0, 999999))
    .max(9999999, G.getShouldBeLessOrEqualLocaleTxt(999999))
    .typeError('Should be a ${type}'), // eslint-disable-line
};

const getMatrixBoundaryValidation = () => Yup.number()
  .nullable(true)
  .transform((value, originalValue) => G.ifElse(G.isNilOrEmpty(originalValue), null, value))
  .min(0, G.getShouldBePositiveLocaleTxt())
  .max(9999999, G.getShouldBeLessOrEqualLocaleTxt(999999))
  .typeError('Should be a ${type}'); // eslint-disable-line

const matrixVariableValidationSchema = Yup.object().shape({
  [GC.FIELD_CHARGE_RATE]: Yup.number()
    .nullable(true)
    .required(G.getRequiredLocaleTxt())
    .min(0, G.getShouldBeFromToLocaleTxt(0, 999999))
    .max(9999999, G.getShouldBeLessOrEqualLocaleTxt(999999))
    .typeError('Should be a ${type}'), // eslint-disable-line
  [GC.FIELD_VALUE_FROM]: getMatrixBoundaryValidation(),
  [GC.FIELD_VALUE_TO]: getMatrixBoundaryValidation().test(
    'matrix-value-to-range',
    G.getWindowLocale(
      'messages:value-to-cannot-be-less-than-value-from',
      'Value To cannot be less than Value From',
    ),
    function validateValueTo(valueTo) {
      const valueFrom = R.prop(GC.FIELD_VALUE_FROM, this.parent);

      if (R.or(R.isNil(valueFrom), R.isNil(valueTo))) return true;

      return R.gte(valueTo, valueFrom);
    },
  ),
}).test(
  'matrix-boundary-required',
  G.getWindowLocale(
    'messages:matrix-boundary-required',
    'Value From or Value To is required',
  ),
  function validateMatrixBoundary(values = {}) {
    const hasBoundary = R.any(
      G.isNotNilAndNotEmpty,
      R.props([GC.FIELD_VALUE_FROM, GC.FIELD_VALUE_TO], values),
    );

    if (hasBoundary) return true;

    return this.createError({
      message: G.getWindowLocale(
        'messages:matrix-boundary-required',
        'Value From or Value To is required',
      ),
      path: `${this.path}.${GC.FIELD_VALUE_FROM}`,
    });
  },
);

const accessorialValidationSchemaObject = {
  [GC.FIELD_CHARGE_RATE_UNIT]: Yup.string().nullable(true),
  [GC.FIELD_ACCESSORIAL_DISTRICT]: Yup.string().nullable(true),
  [GC.FIELD_NAME]: Yup.string().nullable(true).required(G.getRequiredLocaleTxt()),
  [GC.FIELD_CURRENCY]: Yup.string().nullable(true).required(G.getRequiredLocaleTxt()),
  [GC.FIELD_CHARGE_RATE_TYPE]: Yup.string().nullable(true).required(G.getRequiredLocaleTxt()),
  [GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID]: Yup.string().nullable(true).required(G.getRequiredLocaleTxt()),
  [GC.FIELD_MIN_STOPS]: Yup.number()
    .nullable(true)
    .positive(G.getShouldBePositiveLocaleTxt())
    .max(99, G.getShouldBeLessOrEqualLocaleTxt(99)),
  [GC.FIELD_MAX_STOPS]: Yup.number()
    .nullable(true)
    .positive(G.getShouldBePositiveLocaleTxt())
    .max(99, G.getShouldBeLessOrEqualLocaleTxt(99)),
  [GC.FIELD_CHARGE_MIN_RATE]: Yup.number()
    .nullable(true)
    .positive(G.getShouldBePositiveLocaleTxt())
    .max(9999999, G.getShouldBeLessOrEqualLocaleTxt(999999)),
  [GC.FIELD_CHARGE_MAX_RATE]: Yup.number()
    .nullable(true)
    .positive(G.getShouldBePositiveLocaleTxt())
    .max(9999999, G.getShouldBeLessOrEqualLocaleTxt(999999)),
  [GC.FIELD_CHARGE_RATE]: Yup.number()
    .nullable(true)
    .required(G.getRequiredLocaleTxt())
    .positive(G.getShouldBePositiveLocaleTxt())
    .max(9999999, G.getShouldBeLessOrEqualLocaleTxt(999999)),
};

export const getAccessorialValidationSchemaObject = (
  useFuelIndex,
  matrixType,
  fromSharedAccessorialList = false,
) => {
  let schema = accessorialValidationSchemaObject;

  if (G.isFalse(fromSharedAccessorialList)) schema = R.dissoc(GC.FIELD_NAME, schema);

  if (useFuelIndex) {
    schema = R.mergeRight(
      schema,
      {
        [GC.FIELD_TIME_PERIOD]: G.yupStringRequired,
        [GC.FIELD_CHARGE_RATE]: G.yupStringNotRequired,
        [GC.FIELD_ACCESSORIAL_DISTRICT]: G.yupStringRequired,
        [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES]: Yup.array()
          .nullable(true)
          .of(Yup.object(fuelIndexVariablesValidationSchemaObject)),
      },
    );
  }

  if (G.isNotNilAndNotEmpty(matrixType)) {
    schema = R.mergeRight(
      schema,
      {
        [GC.FIELD_CHARGE_RATE]: G.yupStringNotRequired,
        [GC.FIELD_ACCESSORIAL_MATRIX_UOM]: G.yupStringRequired,
        [GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES]: Yup.array()
          .nullable(true)
          .min(1, G.getRequiredLocaleTxt())
          .required(G.getRequiredLocaleTxt())
          .of(matrixVariableValidationSchema),
      },
    );
  }

  return schema;
};

export const fuelIndexVariableFields = [
  {
    isRequired: true,
    label: ['titles:rate'],
    inputWrapperStyles: { mr: 20, width: 230 },
    disabled: (field, { shared }) => shared,
    fieldName: GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES_RATE,
  },
  {
    isRequired: true,
    label: ['titles:index-from'],
    inputWrapperStyles: { mr: 20, width: 230 },
    disabled: (field, { shared }) => shared,
    fieldName: GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES_VALUE_FROM,
  },
  {
    isRequired: true,
    label: ['titles:index-to'],
    inputWrapperStyles: { width: 230 },
    disabled: (field, { shared }) => shared,
    fieldName: GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES_VALUE_TO,
  },
];

export const matrixVariableFields = [
  {
    step: 'any',
    type: 'number',
    isRequired: true,
    label: ['titles:rate'],
    fieldName: GC.FIELD_CHARGE_RATE,
    disabled: (field, { shared }) => shared,
    inputWrapperStyles: { mr: 20, width: 230 },
  },
  {
    step: 'any',
    type: 'number',
    fieldName: GC.FIELD_VALUE_FROM,
    disabled: (field, { shared }) => shared,
    label: ['titles:value-from', 'Value From'],
    inputWrapperStyles: { mr: 20, width: 230 },
  },
  {
    step: 'any',
    type: 'number',
    fieldName: GC.FIELD_VALUE_TO,
    inputWrapperStyles: { width: 230 },
    label: ['titles:value-to', 'Value To'],
    disabled: (field, { shared }) => shared,
  },
];

const fieldName = {
  type: 'text',
  isRequired: true,
  label: ['titles:name'],
  fieldName: GC.FIELD_NAME,
  inputWrapperStyles: { mr: 20, width: 230 },
};

const typesField = {
  isMulti: true,
  type: 'reactSelect',
  fieldName: GC.FIELD_TYPES,
  useMenuPortalTarget: true,
  label: ['titles:types', 'Types'],
  options: 'accessorialTypeOptions',
  inputWrapperStyles: { ml: 20, mt: 25, width: 230 },
};

const autoApplyToField = {
  width: 180,
  zIndex: 10,
  isMulti: true,
  type: 'reactSelect',
  label: ['titles:auto-apply-to'],
  fieldName: GC.FIELD_AUTO_APPLY_TO,
  options: getSharedAccessorialAutoApplyToTypeOptions(),
  inputWrapperStyles: { ml: 20, width: 230, zIndex: 12 },
};

const transportationModeField = {
  width: 180,
  zIndex: 10,
  type: 'reactSelect',
  fieldName: GC.FIELD_MODE,
  options: 'transportationModeOptions',
  label: ['titles:transportation-mode'],
  inputWrapperStyles: { mt: 25, width: 230 },
};

const accessorialSection = [
  {
    isRequired: true,
    type: 'reactSelect',
    shouldCustomChange: true,
    label: ['titles:accessorial'],
    options: 'accessorialConfigOptions',
    inputWrapperStyles: { width: 230, zIndex: 12 },
    fieldName: GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID,
    disabled: (field, { shared }) => shared,
  },
  {
    type: 'toggle',
    disabled: true,
    inputWrapperStyles: { ml: 20 },
    label: ['titles:fuel-related'],
    fieldName: GC.FIELD_FUEL_RELATED,
  },
  {
    type: 'toggle',
    disabled: true,
    alignItems: 'center',
    label: ['titles:non-taxable'],
    inputWrapperStyles: { ml: 8 },
    fieldName: GC.FIELD_NON_TAXABLE,
  },
];

const accessorialDeduction = {
  type: 'toggle',
  label: ['titles:deduction'],
  fieldName: GC.FIELD_DEDUCTION,
  inputWrapperStyles: { ml: 10 },
  disabled: (field, { shared }) => shared,
};

const onlyIncludeToInvoice = {
  type: 'toggle',
  inputWrapperStyles: { ml: 10 },
  fieldName: GC.FIELD_ONLY_INCLUDE_TO_INVOICE,
  label: ['titles:only-include-to-invoice', 'Only Include To Invoice'],
};

const rateSection = [
  {
    step: 'any',
    type: 'number',
    isRequired: true,
    label: ['titles:rate'],
    inputWrapperStyles: { mb: 25, width: 230 },
    fieldName: GC.FIELD_CHARGE_RATE,
    disabled: (field, { shared, useFuelIndex, matrixType }) => R.any(
      G.isTrue,
      [shared, useFuelIndex, G.isNotNilAndNotEmpty(matrixType)],
    ),
  },
  {
    type: 'select',
    isRequired: true,
    inputWrapperStyles,
    label: ['titles:currency'],
    options: GC.CURRENCY_OPTIONS,
    fieldName: GC.FIELD_CURRENCY,
    disabled: (field, { shared }) => shared,
  },
  {
    type: 'select',
    isRequired: true,
    inputWrapperStyles,
    shouldCustomChange: true,
    label: ['titles:rate-type'],
    fieldName: GC.FIELD_CHARGE_RATE_TYPE,
    disabled: (field, { shared }) => shared,
    options: DEFAULT_DRIVER_ASSESSORIAL_RATE_TYPE_OPTIONS,
  },
  {
    type: 'select',
    inputWrapperStyles,
    label: ['titles:rate-unit'],
    options: GC.FIELD_CHARGE_RATE_UNIT,
    fieldName: GC.FIELD_CHARGE_RATE_UNIT,
    disabled: (field, { shared }) => shared,
    optionsGroups: DEFAULT_ACCESSORIAL_FORM_RATE_UNIT_GROUP_OPTIONS,
  },
  {
    step: 'any',
    type: 'number',
    label: ['titles:rate-minimum'],
    fieldName: GC.FIELD_CHARGE_MIN_RATE,
    inputWrapperStyles: { mb: 25, width: 230 },
    disabled: (field, values) => R.or(
      values.shared,
      R.propEq(GC.CHARGE_RATE_TYPE_FLAT, GC.FIELD_CHARGE_RATE_TYPE, values),
    ),
  },
  {
    step: 'any',
    type: 'number',
    inputWrapperStyles,
    label: ['titles:rate-maximum'],
    fieldName: GC.FIELD_CHARGE_MAX_RATE,
    disabled: (field, values) => R.or(
      values.shared,
      R.propEq(GC.CHARGE_RATE_TYPE_FLAT, GC.FIELD_CHARGE_RATE_TYPE, values),
    ),
  },
  {
    step: 'any',
    type: 'number',
    label: ['titles:min-stops'],
    fieldName: GC.FIELD_MIN_STOPS,
    inputWrapperStyles: {
      ...inputWrapperStyles,
      display: ({ values }) => G.ifElse(
        R.propEq(GC.CHARGE_RATE_TYPE_STOP, GC.FIELD_RATE_TYPE, values),
        'flex',
        'none',
      ),
    },
  },
  {
    step: 'any',
    type: 'number',
    label: ['titles:max-stops'],
    fieldName: GC.FIELD_MAX_STOPS,
    inputWrapperStyles: {
      ...inputWrapperStyles,
      display: ({ values }) => G.ifElse(
        R.propEq(GC.CHARGE_RATE_TYPE_STOP, GC.FIELD_RATE_TYPE, values),
        'flex',
        'none',
      ),
    },
  },
];

const carrierRateSection = R.insert(
  2,
  {
    type: 'select',
    isRequired: true,
    inputWrapperStyles,
    shouldCustomChange: true,
    label: ['titles:rate-type'],
    fieldName: GC.FIELD_CHARGE_RATE_TYPE,
    disabled: (field, { shared }) => shared,
    options: R.reject(
      R.propEq(GC.CHARGE_RATE_TYPE_DEADHEAD_DISTANCE, 'value'),
      DEFAULT_DRIVER_ASSESSORIAL_RATE_TYPE_OPTIONS,
    ),
  },
  R.reject(R.propEq(GC.FIELD_CHARGE_RATE_TYPE, 'fieldName'), rateSection),
);

const defaultOrderAccessorialSection = R.insert(
  2,
  {
    type: 'select',
    isRequired: true,
    inputWrapperStyles,
    shouldCustomChange: true,
    label: ['titles:rate-type'],
    fieldName: GC.FIELD_CHARGE_RATE_TYPE,
    disabled: (field, { shared }) => shared,
    options: R.reject(
      R.propEq(GC.CHARGE_RATE_TYPE_DEADHEAD_DISTANCE, 'value'),
      R.dropLast(2, DEFAULT_DRIVER_ASSESSORIAL_RATE_TYPE_OPTIONS),
    ),
  },
  R.reject(R.propEq(GC.FIELD_CHARGE_RATE_TYPE, 'fieldName'), R.dropLast(4, rateSection)),
);

export const districtOptions = [
  GC.EMPTY_OPTION_OBJECT,
  {
    label: 'US',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_US,
  },
  {
    label: 'Midwest',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_MIDWEST,
  },
  {
    label: 'East Cost',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_EAST_COST,
  },
  {
    label: 'Gulf Coast',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_GULF_COAST,
  },
  {
    label: 'West Coast',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_WEST_COAST,
  },
  {
    label: 'California',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_CALIFORNIA,
  },
  {
    label: 'New England',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_NEW_ENGLAND,
  },
  {
    label: 'Rocky Mountain',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_ROCKY_MOUNTAIN,
  },
  {
    label: 'Lower Atlantic',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_LOWER_ATLANTIC,
  },
  {
    label: 'Central Atlantic',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_CENTRAL_ATLANTIC,
  },
  {
    label: 'West Coast Except California',
    value: GC.ACCESSORIAL_DISTRICT_TYPE_WEST_COAST_EXCEPT_CALIFORNIA,
  },
];

export const timePeriodOptions = [
  GC.EMPTY_OPTION_OBJECT,
  {
    value: GC.TIME_PERIOD_TYPE_WEEKLY,
    label: G.getEnumLocale(GC.TIME_PERIOD_TYPE_WEEKLY),
  },
  {
    value: GC.TIME_PERIOD_TYPE_MONTHLY,
    label: G.getEnumLocale(GC.TIME_PERIOD_TYPE_MONTHLY),
  },
  {
    value: GC.TIME_PERIOD_TYPE_LAST_WEEK,
    label: G.getEnumLocale(GC.TIME_PERIOD_TYPE_LAST_WEEK),
  },
];

const matrixSection = (matrixType) => [
  {
    type: 'select',
    shouldCustomChange: true,
    disabled: (field, { shared }) => shared,
    options: ACCESSORIAL_MATRIX_TYPE_OPTIONS,
    inputWrapperStyles: { mb: 25, width: 230 },
    fieldName: GC.FIELD_ACCESSORIAL_MATRIX_TYPE,
    label: ['titles:matrix-type', 'Matrix Type'],
  },
  {
    type: 'select',
    disabled: (field, { shared }) => shared,
    label: ['titles:matrix-uom', 'Matrix UOM'],
    fieldName: GC.FIELD_ACCESSORIAL_MATRIX_UOM,
    isRequired: G.isNotNilAndNotEmpty(matrixType),
    inputWrapperStyles: { mb: 25, ml: 20, width: 230 },
    options: G.ifElse(
      R.equals(matrixType, GC.ACCESSORIAL_MATRIX_TYPE_ITEM_LENGTH),
      ITEM_DIMENSIONS_OPTIONS,
      R.of(Array, GC.EMPTY_OPTION_OBJECT),
    ),
  },
];

const districtSection = (useGeoFencingZone, useFuelIndex, fromSharedAccessorialList) => [
  {
    type: 'select',
    options: districtOptions,
    label: ['titles:district'],
    fieldName: GC.FIELD_ACCESSORIAL_DISTRICT,
    inputWrapperStyles: { mb: 25, width: 230 },
    disabled: (field, { shared }) => shared,
    isRequired: G.ifElse(G.isTrue(useFuelIndex), true, false),
  },
  {
    type: 'select',
    inputWrapperStyles,
    options: timePeriodOptions,
    fieldName: GC.FIELD_TIME_PERIOD,
    label: ['titles:time-period', 'Time Period'],
    disabled: (field, { shared }) => shared,
    isRequired: G.ifElse(G.isTrue(useFuelIndex), true, false),
  },
  {
    type: 'toggle',
    label: ['titles:deduction'],
    fieldName: GC.FIELD_DEDUCTION,
    disabled: (field, { shared }) => shared,
    inputWrapperStyles: {
      mb: 25,
      ml: 20,
      display: G.isTrue(fromSharedAccessorialList) ? 'flex' : 'none',
    },
  },
  {
    type: 'toggle',
    shouldCustomChange: true,
    label: ['titles:use-fuel-index'],
    inputWrapperStyles: { mb: 25, ml: 10 },
    fieldName: GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX,
    disabled: (field, { shared, matrixType }) => R.or(
      shared,
      G.isNotNilAndNotEmpty(matrixType),
    ),
  },
  {
    type: 'toggle',
    shouldCustomChange: true,
    fieldName: 'useGeoFencingZone',
    label: ['titles:use-geo-fencing-zone'],
    disabled: (field, { shared }) => shared,
    inputWrapperStyles: {
      mb: 25,
      ml: 10,
      display: G.ifElse(useGeoFencingZone, 'flex', 'none'),
    },
    customChangeHandler: (event, _, props) => {
      const { values, setValues, handleChange, handleOpenGeoFencingZone } = props;

      if (R.pathEq(true, ['currentTarget', 'checked'], event)) {
        handleOpenGeoFencingZone();
      } else {
        setValues(R.mergeRight(values, { [GC.FIELD_ORIGIN]: null, [GC.FIELD_DESTINATION]: null }));
      }

      handleChange(event);
    },
  },
];

const getAccessorialSection = (options) => {
  if (G.isNilOrEmpty(options)) return accessorialSection;

  const { isDriver, isVendor, showAutoApply, fromSharedAccessorialList } = options;

  if (showAutoApply) {
    return R.append({
      type: 'toggle',
      alignItems: 'center',
      inputWrapperStyles: { ml: 8 },
      fieldName: GC.FIELD_AUTO_APPLY,
      disabled: (field, { shared }) => shared,
      label: ['titles:auto-apply', 'Auto-apply'],
    }, accessorialSection);
  }

  if (fromSharedAccessorialList) {
    return [fieldName, ...accessorialSection, autoApplyToField, transportationModeField, typesField];
  }

  if (isVendor) return R.concat(accessorialSection, [accessorialDeduction, onlyIncludeToInvoice]);

  if (isDriver) return R.append(accessorialDeduction, accessorialSection);

  return accessorialSection;
};

export const accessorialFieldSettings = {
  rateSection,
  matrixSection,
  districtSection,
  carrierRateSection,
  defaultOrderAccessorialSection,
  accessorialSection: getAccessorialSection,
  defaultTripAccessorialSection: R.dropLast(4, rateSection),
};

// geo fencing zone
export const geoFencingZoneFieldSettings = (prefix) => [
  {
    useMenuPortalTarget: true,
    type: 'addressAutocomplete',
    label: ['titles:search-city'],
    fieldName: `${prefix}.searchCity`,
    inputWrapperStyles: geoFencingZoneInputWrapperStyles,
  },
  {
    type: 'multiItems',
    label: ['titles:cities'],
    inputWrapperStyles: geoFencingZoneInputWrapperStyles,
    fieldName: `${prefix}.${GC.FIELD_GEO_FENCING_ZONE_CITIES}`,
    placeholder: G.getWindowLocale('titles:enter-city-and-press-enter', 'Enter City and Press Enter'),
  },
  {
    isMulti: true,
    type: 'reactSelect',
    options: STATE_OPTIONS,
    label: ['titles:states'],
    useMenuPortalTarget: true,
    inputWrapperStyles: geoFencingZoneInputWrapperStyles,
    fieldName: `${prefix}.${GC.FIELD_GEO_FENCING_ZONE_STATES}`,
  },
  {
    isMulti: true,
    type: 'reactSelect',
    options: COUNTRY_OPTIONS,
    useMenuPortalTarget: true,
    label: ['titles:countries'],
    inputWrapperStyles: geoFencingZoneInputWrapperStyles,
    fieldName: `${prefix}.${GC.FIELD_GEO_FENCING_ZONE_COUNTRIES}`,
  },
  {
    useMenuPortalTarget: true,
    type: 'addressAutocomplete',
    label: ['titles:search-zip'],
    fieldName: `${prefix}.searchZip`,
    inputWrapperStyles: geoFencingZoneInputWrapperStyles,
  },
  {
    isMulti: true,
    type: 'multiItems',
    label: ['titles:zip-codes'],
    inputWrapperStyles: geoFencingZoneInputWrapperStyles,
    fieldName: `${prefix}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_CODES}`,
    placeholder: G.getWindowLocale('titles:enter-zip-and-press-enter', 'Enter Zip and Press Enter'),
  },
  {
    label: ['titles:zip-from'],
    inputWrapperStyles: { width: 200 },
    fieldName: `${prefix}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_FROM}`,
  },
  {
    label: ['titles:zip-to'],
    inputWrapperStyles: { width: 200 },
    fieldName: `${prefix}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_TO}`,
  },
];

export const addressPoint = {
  [GC.FIELD_GEO_FENCING_ZONE_CITIES]: [],
  [GC.FIELD_GEO_FENCING_ZONE_STATES]: [],
  [GC.FIELD_GEO_FENCING_ZONE_ZIP_CODES]: [],
  [GC.FIELD_GEO_FENCING_ZONE_COUNTRIES]: null,
  [GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_TO]: null,
  [GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_FROM]: null,
};

export const defaultGeoFencingZoneFieldSettings = {
  [GC.FIELD_ORIGIN]: addressPoint,
  [GC.FIELD_DESTINATION]: addressPoint,
};

export const geoFencingZoneValidationSchema = () => Yup.lazy(({ origin, destination }) => {
  const getShouldRequired = R.compose(
    R.lt(0),
    R.length,
    R.values,
    R.filter(G.isNotNilAndNotEmpty),
    R.pick(R.keys(addressPoint)),
  );

  return Yup.object().shape({
    [GC.FIELD_ORIGIN]: Yup.object().nullable(true).shape({
      [GC.FIELD_GEO_FENCING_ZONE_COUNTRIES]: G.ifElse(
        getShouldRequired(origin),
        G.yupArrayRequired,
        G.yupArrayNotRequired,
      ),
    }),
    [GC.FIELD_DESTINATION]: Yup.object().nullable(true).shape({
      [GC.FIELD_GEO_FENCING_ZONE_COUNTRIES]: G.ifElse(
        getShouldRequired(destination),
        G.yupArrayRequired,
        G.yupArrayNotRequired,
      ),
    }),
  });
});
